import BooleanTag from '@/components/app/BooleanTag';
import ExportBtn from '@/components/app/ExportBtn';
import CompanyInput from '@/components/FormItems/CompanyInput';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import ZTable, { IFilterItem, IColumnType, ITableRef } from '@/components/ZTable';
import { formatDisplayDate } from '@/config';
import { GenderType } from '@/constants';
import i18n from '@/helpers/i18n';
import MainContent from '@/layouts/MainContent';
import { exportService } from '@/services/exportService';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getFullName } from '@/helpers/string';
import CountryInput from '@/components/FormItems/CountryInput';
import { PassengerDetailModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'passengerPage_Title';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', mdSize: 8, xlSize: 8, xxlSize: 8, label: 'passengerPage__SearchText' },
  {
    type: 'SELECT',
    name: 'companyId',
    label: 'base.Company',
    customElm: <CompanyInput placeholder={i18n.t('base.Company') as any} />,
  },
  { type: 'SELECT', name: 'countryId', label: 'base.Country', mdSize: 8, customElm: <CountryInput /> },
];

const columns: IColumnType<PassengerDetailModel>[] = [
  {
    title: 'base.FullName',
    dataIndex: 'id',
    ellipsis: true,
    sorter: true,
    defaultSortOrder: 'ascend',
    render: (v, record) => getFullName(record.firstName, record.lastName),
    width: 200,
  },
  {
    title: 'base.Email',
    dataIndex: 'emailAddress',
    sorter: true,
    ellipsis: true,
    width: 200,
  },
  {
    title: 'base.Country',
    dataIndex: 'countryName',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNumber',
    render: (_, record) => [record.phoneCode, record.phoneNumber].join(' '),
    ellipsis: true,
    width: 150,
  },
  {
    title: 'base.DateOfBirth',
    dataIndex: 'dateOfBirth',
    width: 120,
    ellipsis: true,
    render: v => (v ? formatDisplayDate(v) : ''),
    sorter: true,
  },
  {
    title: 'passengersPage_PassengerType',
    dataIndex: 'passengerType',
    ellipsis: true,
    width: 120,
  },
  {
    title: 'base.Gender',
    dataIndex: 'gender',
    width: 100,
    ellipsis: true,
    render: v => GenderType.__getValue(v),
    sorter: true,
  },

  {
    title: 'passengersPage_IsTicketBookingAllowed',
    dataIndex: 'isTicketBookingAllowed',
    sorter: true,
    width: 120,
    align: 'center',
    render: v => <BooleanTag value={v} />,
    ellipsis: true,
  },
];
export default function PassengersPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);

  React.useEffect(() => {
    apiSdk.BookingPassengerService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportPassengers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportPassengers({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toPdf();
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <ExportBtn
          disabled={!exportColumns?.length}
          onClick={() => {
            toggleExportModal(true);
          }}
        />,
      ]}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/bookings/booking-passengers/get-passengers"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          searchBtnAlignLeft
        ></ZTable>
      </div>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('passengerPage_ExportPassengers')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
    </MainContent>
  );
}
